import { DateTime } from "luxon";
import { useMemo } from "react";
import { AccessibleContent } from "../../accessibility";
import { formatDate, formatTimeOfDay } from "../../formatters";
import { useDesignSystemI18n } from "../../providers/DesignSystemI18nContext";
import { useLocale } from "../../providers/DesignSystemLocaleContext";
import { Text } from "../../typography";
import type { PermitIntervalType } from "./ParkingOption";
import { ScheduleLabelChip } from "./ScheduleLabelChip";
import { TimeBadge } from "./TimeBadge";
import { type ComparisonSide, changedValueClassName, valueIntent } from "./comparison";

export const getIntervalType = ({
	startDateTime,
	endDateTime,
	timezone,
}: { startDateTime: string; endDateTime?: string | null; timezone: string }): PermitIntervalType => {
	if (!endDateTime) {
		return "DATE_RANGE";
	}
	const start = DateTime.fromISO(startDateTime).setZone(timezone).startOf("day");
	const end = DateTime.fromISO(endDateTime).setZone(timezone).startOf("day");
	return start.equals(end) ? "SINGLE" : "DATE_RANGE";
};

const DAYS_OF_WEEK = [
	{ key: "MONDAY", label: "Mon", name: "monday" },
	{ key: "TUESDAY", label: "Tue", name: "tuesday" },
	{ key: "WEDNESDAY", label: "Wed", name: "wednesday" },
	{ key: "THURSDAY", label: "Thu", name: "thursday" },
	{ key: "FRIDAY", label: "Fri", name: "friday" },
	{ key: "SATURDAY", label: "Sat", name: "saturday" },
	{ key: "SUNDAY", label: "Sun", name: "sunday" },
] as const;

const DISPLAY_MODE = {
	DATE_LABELS: "dateLabels",
	DAY_LABELS: "dayLabels",
} as const;

type DayOfWeek = (typeof DAYS_OF_WEEK)[number]["key"];

type DateRow = { dateISO: string; label: string; dayKey: DayOfWeek };

type DateRowLayout = {
	displayMode: (typeof DISPLAY_MODE)[keyof typeof DISPLAY_MODE];
	dateRows: DateRow[];
};

type Schedule = {
	startTime: string;
	endTime: string;
	day: DayOfWeek;
};

const toDateRows = ({
	startDateTimeISO,
	endDateTimeISO,
	timezone,
}: {
	startDateTimeISO: string;
	endDateTimeISO?: string | null;
	timezone: string;
}): DateRowLayout => {
	const startDay = DateTime.fromISO(startDateTimeISO).setZone(timezone).startOf("day");

	if (!endDateTimeISO) {
		return { displayMode: DISPLAY_MODE.DAY_LABELS, dateRows: [] };
	}

	const endDay = DateTime.fromISO(endDateTimeISO).setZone(timezone).startOf("day");

	const daysToInclude = Math.floor(endDay.diff(startDay, "days").days) + 1;

	if (daysToInclude > 0 && daysToInclude < 7) {
		const dateRows: DateRow[] = [];
		for (let dayOffset = 0; dayOffset < daysToInclude; dayOffset++) {
			const date = startDay.plus({ days: dayOffset });

			const weekdayEntry = DAYS_OF_WEEK[date.weekday - 1]!;
			const dateISO = date.toISO()!;

			dateRows.push({
				dateISO,
				label: formatDate({
					date: dateISO,
					formatType: "Abbreviated_Date_No_Year",
					timezone,
				}),
				dayKey: weekdayEntry.key,
			});
		}
		return { displayMode: DISPLAY_MODE.DATE_LABELS, dateRows };
	}

	return { displayMode: DISPLAY_MODE.DAY_LABELS, dateRows: [] };
};

type ParkingTimeScheduleProps = {
	interval: {
		type: PermitIntervalType;
		startDateTimeISO: string;
		endDateTimeISO?: string | undefined;
		schedules: Schedule[];
	};
	timezone: string;
	hideSection?: "days" | "times";
	comparison?: {
		side: ComparisonSide;
		other?: {
			startDateTimeISO: string;
			endDateTimeISO?: string | null;
			schedules: Schedule[];
			timezone: string;
		};
	};
};

export const ParkingTimeSchedule = ({ interval, timezone, hideSection, comparison }: ParkingTimeScheduleProps) => {
	const { parkingOption, daysOfWeek } = useDesignSystemI18n();
	const { locale } = useLocale();
	const { schedules, startDateTimeISO, endDateTimeISO } = interval;

	const side = comparison?.side;
	const otherSchedules = comparison?.other?.schedules ?? [];
	const timeText = (time: string) => formatTimeOfDay({ time, locale });
	const isRangeChanged = (days: DayOfWeek[], range: { start: string; end: string }) =>
		!!comparison &&
		days.some(
			(day) =>
				!otherSchedules.some(
					(other) =>
						other.day.toUpperCase() === day &&
						timeText(other.startTime) === timeText(range.start) &&
						timeText(other.endTime) === timeText(range.end),
				),
		);
	const allDayClassName = (days: DayOfWeek[], ranges: { start: string; end: string }[]) =>
		changedValueClassName(
			side,
			ranges.some((range) => isRangeChanged(days, range)),
		);
	const textIntent = valueIntent(side);
	const everyDay = DAYS_OF_WEEK.map(({ key }) => key);
	const otherDateLabels = new Set(
		comparison?.other ? toDateRows(comparison.other).dateRows.map(({ label }) => label) : [],
	);
	const hasOtherScheduleOn = (day: DayOfWeek) => otherSchedules.some((other) => other.day.toUpperCase() === day);
	const everyDayLabelClassName = changedValueClassName(side, !everyDay.every(hasOtherScheduleOn));

	const { displayMode, dateRows } = useMemo(
		() => toDateRows({ startDateTimeISO, endDateTimeISO, timezone }),
		[startDateTimeISO, endDateTimeISO, timezone],
	);

	const rowLabelClassName = (dayKey: DayOfWeek, label: string) =>
		changedValueClassName(
			side,
			displayMode === DISPLAY_MODE.DATE_LABELS ? !otherDateLabels.has(label) : !hasOtherScheduleOn(dayKey),
		);

	const { allDaysAreEqual, groupedSchedules } = useMemo(() => {
		let groupedSchedules: { [key: string]: { timeRanges: { start: string; end: string }[] } } = {};
		let allDaysAreEqual = true;
		let prevStartTime = undefined;
		let prevEndTime = undefined;

		for (const schedule of schedules) {
			const day = schedule.day.toUpperCase();
			if (!groupedSchedules[day]) {
				groupedSchedules[day] = { timeRanges: [] };
			}

			const currStartTime = schedule.startTime;
			const currEndTime = schedule.endTime;

			if (
				schedules.length !== 7 ||
				(prevStartTime && prevEndTime && (prevStartTime !== currStartTime || prevEndTime !== currEndTime))
			) {
				allDaysAreEqual = false;
			}

			prevStartTime = currStartTime;
			prevEndTime = currEndTime;

			groupedSchedules = {
				...groupedSchedules,
				[day]: {
					timeRanges: [
						...(groupedSchedules[day]?.timeRanges || []),
						{ start: schedule.startTime, end: schedule.endTime },
					],
				},
			};
		}

		return {
			allDaysAreEqual,
			groupedSchedules,
		};
	}, [schedules]);

	// Condense all days into a single row if the schedule less than 7 days and is the same every day
	if (displayMode === DISPLAY_MODE.DAY_LABELS && allDaysAreEqual && Object.keys(groupedSchedules).length > 0) {
		const everyDayRanges = groupedSchedules.MONDAY?.timeRanges;
		const isActiveAllDay = everyDayRanges?.every((range) => range.start === "00:00" && range.end === "23:59");
		const timeLabel = isActiveAllDay
			? parkingOption.allDay
			: everyDayRanges
					?.map(
						(range) =>
							`${formatTimeOfDay({ time: range.start, locale })} - ${formatTimeOfDay({ time: range.end, locale })}`,
					)
					.join(", ");

		if (hideSection === "times") {
			const rangeCount = everyDayRanges?.length ?? 1;
			return (
				<div className="space-y-1">
					<ScheduleLabelChip
						side={side}
						className={everyDayLabelClassName}
						label={parkingOption.everyDay}
						style={{ minHeight: `${rangeCount * 24 + (rangeCount - 1) * 4}px` }}
					/>
				</div>
			);
		}

		if (hideSection === "days") {
			const ranges = everyDayRanges ?? [];
			return (
				<div className="space-y-1">
					{ranges.map((range) => (
						<div key={`${range.start}-${range.end}`} className="flex min-h-6 items-center justify-start">
							{isActiveAllDay ? (
								<Text size="xs" intent={textIntent} className={allDayClassName(everyDay, [range])}>
									{parkingOption.allDay}
								</Text>
							) : (
								<TimeBadge side={side} changed={isRangeChanged(everyDay, range)}>
									{formatTimeOfDay({ time: range.start, locale })} {" - "}
									{formatTimeOfDay({ time: range.end, locale })}
								</TimeBadge>
							)}
						</div>
					))}
				</div>
			);
		}

		return (
			<AccessibleContent label={`${parkingOption.everyDay}: ${timeLabel}`} As="p">
				<div className="grid grid-cols-[60px_1fr] gap-4" aria-label={`${parkingOption.everyDay} ${timeLabel}`}>
					<ScheduleLabelChip side={side} className={everyDayLabelClassName} label={parkingOption.everyDay} />

					<div className="flex items-center justify-start">
						<div className="flex flex-col items-start gap-1">
							{isActiveAllDay ? (
								<Text
									size="xs"
									intent={textIntent}
									className={allDayClassName(everyDay, everyDayRanges ?? [])}
								>
									{parkingOption.allDay}
								</Text>
							) : (
								everyDayRanges?.map((range) => (
									<TimeBadge
										key={`${range.start}-${range.end}`}
										side={side}
										changed={isRangeChanged(everyDay, range)}
									>
										{formatTimeOfDay({ time: range.start, locale })} {" - "}
										{formatTimeOfDay({ time: range.end, locale })}
									</TimeBadge>
								))
							)}
						</div>
					</div>
				</div>
			</AccessibleContent>
		);
	}

	const schedulesToRender =
		displayMode === DISPLAY_MODE.DATE_LABELS
			? dateRows.map((row) => ({
					key: row.dateISO,
					label: row.label,
					screenReaderLabel: formatDate({
						date: row.dateISO,
						formatType: "Abbreviated_Date_No_Year",
						timezone,
						language: locale,
					}),
					dayKey: row.dayKey,
					daySchedule: groupedSchedules[row.dayKey],
				}))
			: DAYS_OF_WEEK.map((dayOfWeek) => ({
					key: dayOfWeek.key,
					label: dayOfWeek.label,
					screenReaderLabel: daysOfWeek[dayOfWeek.name],
					dayKey: dayOfWeek.key,
					daySchedule: groupedSchedules[dayOfWeek.key],
				}));

	if (hideSection === "times") {
		const filtered = schedulesToRender.filter(({ daySchedule }) => daySchedule);
		return (
			<div className="space-y-1">
				{filtered.map(({ key, label, dayKey, daySchedule }) => {
					const rangeCount = daySchedule?.timeRanges?.length ?? 1;
					return (
						<ScheduleLabelChip
							side={side}
							className={rowLabelClassName(dayKey, label)}
							key={key}
							label={label}
							style={{ minHeight: `${rangeCount * 24 + (rangeCount - 1) * 4}px` }}
						/>
					);
				})}
			</div>
		);
	}

	if (hideSection === "days") {
		const flatEntries = schedulesToRender
			.filter(({ daySchedule }) => daySchedule)
			.flatMap(({ key, dayKey, daySchedule }) => {
				const ranges = daySchedule?.timeRanges ?? [];
				if (ranges.length === 0) {
					return [];
				}
				return ranges.map((range) => ({
					key: `${key}-${range.start}-${range.end}`,
					dayKey,
					range,
					isAllDay: range.start === "00:00" && range.end === "23:59",
				}));
			});

		return (
			<div className="space-y-1">
				{flatEntries.map(({ key, dayKey, range, isAllDay }) => (
					<div key={key} className="flex min-h-6 items-center justify-start">
						{isAllDay ? (
							<Text size="xs" intent={textIntent} className={allDayClassName([dayKey], [range])}>
								{parkingOption.allDay}
							</Text>
						) : (
							<TimeBadge side={side} changed={isRangeChanged([dayKey], range)}>
								{formatTimeOfDay({ time: range.start, locale })} {" - "}
								{formatTimeOfDay({ time: range.end, locale })}
							</TimeBadge>
						)}
					</div>
				))}
			</div>
		);
	}

	return (
		<div className="flex flex-col gap-1">
			{schedulesToRender
				.filter(({ daySchedule }) => daySchedule)
				.map(({ key, label, screenReaderLabel, dayKey, daySchedule }) => {
					const isActiveAllDay = daySchedule?.timeRanges?.every(
						(range) => range.start === "00:00" && range.end === "23:59",
					);

					const timeLabel = isActiveAllDay
						? parkingOption.allDay
						: daySchedule?.timeRanges
								?.map(
									(range) =>
										`${formatTimeOfDay({ time: range.start, locale })} - ${formatTimeOfDay({ time: range.end, locale })}`,
								)
								.join(", ");

					return (
						<AccessibleContent key={key} label={`${screenReaderLabel}: ${timeLabel}`} As="p">
							<div className="grid grid-cols-[60px_1fr] gap-4" aria-label={`${label} ${timeLabel}`}>
								<ScheduleLabelChip
									side={side}
									className={rowLabelClassName(dayKey, label)}
									label={label}
								/>

								<div className="flex items-center justify-start">
									<div className="flex flex-col items-start gap-1">
										{isActiveAllDay ? (
											<Text
												size="xs"
												intent={textIntent}
												className={allDayClassName([dayKey], daySchedule?.timeRanges ?? [])}
											>
												{parkingOption.allDay}
											</Text>
										) : (
											daySchedule?.timeRanges?.map((range) => (
												<TimeBadge
													key={`${range.start}-${range.end}`}
													side={side}
													changed={isRangeChanged([dayKey], range)}
												>
													{formatTimeOfDay({ time: range.start, locale })} {" - "}
													{formatTimeOfDay({ time: range.end, locale })}
												</TimeBadge>
											))
										)}
									</div>
								</div>
							</div>
						</AccessibleContent>
					);
				})}
		</div>
	);
};
